import { ScheduleDay, ScheduleRule } from "../../../../../utils/api/classroomSchedules";
import LineReader from "../../../../../utils/LineReader";
import { readScheduleCSVLineToType } from "./utils";

export type ScheduleCSVEntry = {
  classroom_name: string;
  teacher_username: string;
  day: string;
  start_time: string;
  end_time: string;
  end_date: string;
};

export class ScheduleCSVObject {
  file: File;
  entries: Set<ScheduleCSVEntry>;
  unique_classrooms: Set<string>;
  linesRead: number;
  processed: boolean;
  hasError: boolean;
  errorMessage: string;
  invalidRows: number;
  instanceId: string;
  errorCallbackCalled: boolean;

  constructor(file: File) {
    this.file = file;
    this.entries = new Set<ScheduleCSVEntry>();
    this.unique_classrooms = new Set<string>();
    this.linesRead = 0;
    this.processed = false;
    this.hasError = false;
    this.errorMessage = "";
    this.invalidRows = 0;
    this.instanceId = Math.random().toString(36).substr(2, 9);
    this.errorCallbackCalled = false;
  }

  getFile = (): File => {
    return this.file;
  };

  private readLine = (line: string, callback: () => void): void => {
    this.linesRead++;

    // Skip empty lines
    if (!line.trim()) {
      callback();
      return;
    }

    try {
      const entry = readScheduleCSVLineToType(line);
      this.addEntry(entry);
      callback();
      return;
    } catch (error) {
      this.hasError = true;
      this.invalidRows++;
      // Only set the error message once (for the first error)
      if (!this.errorMessage) {
        this.errorMessage = `Expected 6 columns per row. Please check your CSV format.`;
      }
      callback();
      return;
    }
  };

  private addEntry = (entry: ScheduleCSVEntry): void => {
    this.entries.add(entry);
    this.unique_classrooms.add(entry.classroom_name);
  };

  getUniqueEntries = (): { classrooms: number; periods: number } => {
    return {
      classrooms: this.unique_classrooms.size,
      periods: this.entries.size,
    };
  };

  getHasError = (): boolean => {
    return this.hasError;
  };

  getErrorMessage = (): string => {
    if (this.hasError && this.invalidRows > 0) {
      return `${this.errorMessage} Found ${this.invalidRows} invalid row${this.invalidRows > 1 ? "s" : ""} out of ${
        this.linesRead
      } total rows.`;
    }
    return this.errorMessage;
  };

  getInvalidRowsCount = (): number => {
    return this.invalidRows;
  };

  getScheduleRules = (): ScheduleRule[] => {
    return Array.from(this.entries).map((entry) => {
      return {
        className: entry.classroom_name,
        username: entry.teacher_username,
        day: entry.day as ScheduleDay,
        startTime: entry.start_time,
        endTime: entry.end_time,
        endDate: entry.end_date,
      };
    });
  };

  public parseFileContents = (
    onProgress?: (progress: number) => void,
    onComplete?: (csvObject: ScheduleCSVObject) => void,
    onError?: (csvObject: ScheduleCSVObject) => void
  ): void => {
    if (this.processed) {
      if (this.hasError && !this.errorCallbackCalled) {
        console.log(`ScheduleCSVObject ${this.instanceId} already processed with error, calling error callback`);
        this.errorCallbackCalled = true;
        onError?.(this);
      } else if (!this.hasError) {
        onComplete?.(this);
      }
      return;
    }

    const lr = new LineReader();

    lr.on("line", (line: string, callback: () => void) => {
      this.readLine(line, callback);

      // Continue processing even if there are errors - we want to process the entire file
      // and only call the error callback once at the end
    });

    if (onProgress) {
      lr.on("progress", onProgress);
    }

    lr.on("end", () => {
      this.processed = true;
      if (this.hasError && !this.errorCallbackCalled) {
        console.log(`ScheduleCSVObject ${this.instanceId} calling error callback with ${this.invalidRows} invalid rows`);
        this.errorCallbackCalled = true;
        onError?.(this);
      } else if (!this.hasError) {
        console.log(`ScheduleCSVObject ${this.instanceId} calling complete callback`);
        onComplete?.(this);
      } else {
        console.log(`ScheduleCSVObject ${this.instanceId} error callback already called, skipping`);
      }
    });

    lr.read(this.file);
  };

  setProcessed = (): void => {
    this.processed = true;
  };
}
